import { apiClient } from '@borradh-workspace/api-client';
import { metaCampaignInsightsSchema } from '@borradh-workspace/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { CampaignInsights, CampaignInsightsParams } from '../types';

/**
 * Query options for getting campaign insights
 * campaignId is the Meta campaign ID
 */
export const getCampaignInsightsQueryOptions = (
  campaignId: string,
  params: CampaignInsightsParams = {}
) => {
  const searchParams = new URLSearchParams();
  if (params.since) searchParams.set('since', params.since);
  if (params.until) searchParams.set('until', params.until);
  const qs = searchParams.toString();

  return queryOptions({
    queryKey: ['meta-campaigns', campaignId, 'insights', params],
    queryFn: async () => {
      return apiClient.get<CampaignInsights>(
        `meta-campaigns/${campaignId}/insights${qs ? `?${qs}` : ''}`,
        { schema: metaCampaignInsightsSchema }
      );
    },
    enabled: !!campaignId,
    staleTime: 5 * 60 * 1000, // 5 minutes — reduce Meta API calls to avoid rate limits
  });
};

interface UseGetCampaignInsightsOptions {
  campaignId: string;
  params?: CampaignInsightsParams;
  enabled?: boolean;
}

/**
 * Get campaign insights hook
 * Returns performance metrics for a campaign from Meta Ads
 * campaignId is the Meta campaign ID
 */
export const useGetCampaignInsights = ({
  campaignId,
  params = {},
  enabled = true,
}: UseGetCampaignInsightsOptions) => {
  const query = useQuery({
    ...getCampaignInsightsQueryOptions(campaignId, params),
    enabled: enabled && !!campaignId,
  });

  return {
    insights: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Lifetime per-ad insights for one campaign, keyed by Meta ad ID. Rides the
 * campaign insights query, so the ads list and an ad's metrics panel share one
 * Meta request.
 *
 * `totals` is for `metaAdId`: an ad with no row after a successful load had
 * no delivery (empty totals); an ad not yet on Meta has none (undefined).
 */
export const useCampaignAdInsights = (
  campaignId: string,
  metaAdId?: string | null
) => {
  const { insights, isLoading, isError, refetch } = useGetCampaignInsights({
    campaignId,
  });

  const insightsByAdId = useMemo(
    () => new Map((insights?.ads ?? []).map((row) => [row.adId, row])),
    [insights]
  );

  const totalsFor = (adId?: string | null) =>
    !adId || isLoading || isError
      ? undefined
      : (insightsByAdId.get(adId) ?? {});

  return {
    totalsFor,
    totals: totalsFor(metaAdId),
    /** The ad account's currency, as Meta reported it with these figures. */
    currency: insights?.currency,
    isLoading,
    isError,
    refetch,
  };
};
